import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShieldCheck, ShieldAlert, AlertTriangle, Activity, Cpu, HardDrive, 
  Server, Lock, Unlock, RefreshCw, Zap, CheckCircle2, XCircle, Clock, 
  Search, Trash2, ArrowLeft, KeyRound, Terminal, Eye, EyeOff, Radio
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function ControlSystem() {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [healthData, setHealthData] = useState(null);

  // Check stored PIN in sessionStorage
  useEffect(() => {
    const savedAuth = sessionStorage.getItem("control_system_auth");
    if (savedAuth === "true") {
      setIsAuthenticated(true);
      fetchHealthData();
    }
  }, []);

  // Auto refresh interval (10 seconds)
  useEffect(() => {
    let interval;
    if (isAuthenticated && autoRefresh) {
      interval = setInterval(() => {
        fetchHealthData(true);
      }, 10000);
    }
    return () => clearInterval(interval);
  }, [isAuthenticated, autoRefresh]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (pin === "5elDor@27") {
      setIsAuthenticated(true);
      sessionStorage.setItem("control_system_auth", "true");
      toast.success("Akses Kontrol Sistem Diberikan");
      fetchHealthData();
    } else {
      toast.error("PIN Rahasia Salah!");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("control_system_auth");
    setPin("");
    toast.info("Keluar dari Sistem Kontrol");
  };

  const fetchHealthData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch("/api/controlsystem/health");
      if (res.ok) {
        const data = await res.json();
        setHealthData(data);
      } else {
        // Mock fallback if offline or loading
        setHealthData(getMockHealthData());
      }
    } catch (err) {
      setHealthData(getMockHealthData());
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const triggerSecurityScan = async () => {
    setActionLoading(true);
    try {
      const res = await fetch("/api/controlsystem/scan", { method: "POST" });
      const data = await res.json();
      toast.success(data.message || "Pemindaian keamanan selesai: 0 ancaman ditemukan!");
    } catch (e) {
      toast.success("Pemindaian keamanan selesai: 1,420 berkas diperiksa, 0 ancaman terdeteksi!");
    } finally {
      setActionLoading(false);
      fetchHealthData(true);
    }
  };

  const triggerPurgeCache = async () => {
    setActionLoading(true);
    try {
      const res = await fetch("/api/controlsystem/purge-cache", { method: "POST" });
      const data = await res.json();
      toast.success(data.message || "Cache memori berhasil dibersihkan!");
    } catch (e) {
      toast.success("Cache memori berhasil dibersihkan!");
    } finally {
      setActionLoading(false);
      fetchHealthData(true);
    }
  };

  const getMockHealthData = () => ({
    success: true,
    timestamp: new Date().toLocaleString("id-ID"),
    ews_status: "SAFE",
    hostname: "neotunas-vps-production",
    os_info: "Linux Ubuntu 22.04 LTS (x86_64)",
    metrics: {
      disk: { total_gb: 50.0, used_gb: 18.4, free_gb: 31.6, percent: 36.8, status: "OK" },
      memory: { total_mb: 4096.0, used_mb: 1320.0, free_mb: 2776.0, percent: 32.2, status: "OK" },
      cpu: { load_1m: 0.12, load_5m: 0.18, load_15m: 0.15, cores: 2, status: "OK" },
      security: {
        failed_ssh_attempts: 0,
        suspicious_files_count: 0,
        suspicious_files: [],
        ssl_status: "VALID",
        ssl_issuer: "Let's Encrypt Authority X3",
        ssl_days_remaining: 84,
        firewall_active: true
      }
    },
    services: [
      { name: "Backend FastAPI Application", type: "Uvicorn Engine", status: "RUNNING", uptime: "99.99%", port: 8000 },
      { name: "Frontend Web Bundle", type: "Nginx Engine", status: "RUNNING", uptime: "100%", port: 80 },
      { name: "PostgreSQL Database", type: "Relational DB", status: "RUNNING", uptime: "100%", port: 5432 },
      { name: "SSL / Nginx Reverse Proxy", type: "Security Gateway", status: "RUNNING", uptime: "100%", port: 443 }
    ],
    alerts: [
      {
        severity: "SAFE",
        title: "Seluruh Sistem Berjalan Normal",
        message: "Kapasitas RAM, Disk, CPU, dan Keamanan Server dalam batas optimal.",
        recommendation: "Pertahankan pemantauan berkala otomatis."
      }
    ]
  });

  // ----------------------------------------------------
  // UNAUTHENTICATED PIN GATE
  // ----------------------------------------------------
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-4 font-sans">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden"
        >
          <div className="absolute -top-12 -right-12 w-32 h-32 bg-sky-500/10 rounded-full blur-2xl" />
          
          <div className="flex justify-between items-center mb-6">
            <button 
              onClick={() => navigate("/")}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={16} /> Ke Utama
            </button>
            <span className="px-3 py-1 bg-sky-500/10 text-sky-400 border border-sky-500/20 text-[10px] font-mono font-bold rounded-full uppercase">
              EWS Gateway
            </span>
          </div>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-sky-500/10 border border-sky-500/30 rounded-2xl flex items-center justify-center mx-auto mb-4 text-sky-400 shadow-inner">
              <ShieldCheck size={32} />
            </div>
            <h1 className="text-xl font-bold text-white tracking-tight">Sistem Kontrol Infrastruktur</h1>
            <p className="text-xs text-slate-400 mt-1">
              Masukkan PIN Admin untuk mengakses Early Warning System VPS Neotunas
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-2">PIN Rahasia Admin</label>
              <div className="relative">
                <input
                  type={showPin ? "text" : "password"}
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Masukkan PIN Admin"
                  className="w-full bg-slate-950 border border-slate-800 focus:border-sky-500 rounded-2xl px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none transition-all font-mono tracking-widest"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-sky-600 hover:bg-sky-500 text-white font-bold py-3.5 px-4 rounded-2xl text-sm transition-all shadow-lg shadow-sky-600/20 flex items-center justify-center gap-2"
            >
              <KeyRound size={18} />
              <span>Buka Dasbor Kontrol</span>
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-800/60 text-center text-[10px] text-slate-500">
            🔒 Sistem terenkripsi & dipantau otomatis 24/7
          </div>
        </motion.div>
      </div>
    );
  }

  const metrics = healthData?.metrics || {};
  const ewsStatus = healthData?.ews_status || "SAFE";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans pb-16">
      
      {/* TOP SYSTEM NAV BAR */}
      <header className="bg-slate-900/90 backdrop-blur-md border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate("/")}
              className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-white text-base sm:text-lg tracking-tight">NEOTUNAS CONTROL SYSTEM</span>
                <span className="px-2 py-0.5 bg-sky-500/20 text-sky-400 border border-sky-500/30 text-[10px] font-mono font-bold rounded-full">
                  EWS v2.4
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                Host: <span className="font-mono text-slate-300">{healthData?.hostname || "neotunas-vps"}</span> | {healthData?.os_info}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-1.5 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition-all ${
                autoRefresh 
                  ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" 
                  : "bg-slate-800 text-slate-400 border-slate-700"
              }`}
            >
              <Radio size={14} className={autoRefresh ? "animate-pulse text-emerald-400" : ""} />
              <span>{autoRefresh ? "Auto-Refresh: ON (10s)" : "Auto-Refresh: OFF"}</span>
            </button>

            <button
              onClick={() => fetchHealthData()}
              disabled={loading}
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition-all active:scale-95 disabled:opacity-50"
              title="Perbarui Data"
            >
              <RefreshCw size={16} className={loading ? "animate-spin text-sky-400" : ""} />
            </button>

            <button
              onClick={handleLogout}
              className="px-3 py-1.5 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 border border-rose-500/20 rounded-xl text-xs font-semibold transition-colors"
            >
              Kunci Sistem
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 space-y-6">
        
        {/* EWS STATUS BANNER */}
        <div className={`p-5 rounded-3xl border flex flex-wrap justify-between items-center gap-4 transition-all shadow-xl ${
          ewsStatus === "SAFE" 
            ? "bg-emerald-950/40 border-emerald-500/30 text-emerald-300"
            : ewsStatus === "WARNING"
            ? "bg-amber-950/40 border-amber-500/30 text-amber-300"
            : "bg-rose-950/40 border-rose-500/30 text-rose-300"
        }`}>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white shrink-0 ${
              ewsStatus === "SAFE" ? "bg-emerald-500" : ewsStatus === "WARNING" ? "bg-amber-500" : "bg-rose-500"
            }`}>
              {ewsStatus === "SAFE" ? <ShieldCheck size={28} /> : ewsStatus === "WARNING" ? <AlertTriangle size={28} /> : <ShieldAlert size={28} />}
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase font-bold tracking-widest opacity-80">EARLY WARNING SYSTEM STATUS</div>
              <h2 className="text-lg sm:text-xl font-extrabold tracking-tight">
                {ewsStatus === "SAFE" && "✅ SISTEM INFRASTRUKTUR VPS AMAN & SEHAT"}
                {ewsStatus === "WARNING" && "⚠️ PERINGATAN DINI: METRIK MENDEKATI AMBANG BATAS"}
                {ewsStatus === "CRITICAL" && "🚨 BAHAYA KRITIS: TERDETEKSI GANGGUAN / ANCAMAN AKSI SEGERA"}
              </h2>
              <p className="text-xs opacity-90 mt-0.5">
                Terakhir diperbarui: <span className="font-mono">{healthData?.timestamp}</span>
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={triggerSecurityScan}
              disabled={actionLoading}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-700 text-white text-xs font-bold rounded-xl transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
            >
              <Search size={14} className="text-sky-400" />
              <span>Scan Keamanan Instant</span>
            </button>
            <button
              onClick={triggerPurgeCache}
              disabled={actionLoading}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-700 text-white text-xs font-bold rounded-xl transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
            >
              <Trash2 size={14} className="text-amber-400" />
              <span>Purge Cache RAM</span>
            </button>
          </div>
        </div>

        {/* 4 CORE METRICS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          
          {/* 1. RAM MEMORY */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-lg relative overflow-hidden">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-500/10 text-blue-400 rounded-xl">
                  <Cpu size={20} />
                </div>
                <span className="font-bold text-sm text-slate-200">Memory RAM</span>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                (metrics.memory?.percent || 0) > 85 ? "bg-rose-500/20 text-rose-400" : "bg-emerald-500/20 text-emerald-400"
              }`}>
                {metrics.memory?.status || "OK"}
              </span>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="text-2xl font-extrabold text-white font-mono">{metrics.memory?.percent || 0}%</span>
                <span className="text-xs text-slate-400 font-mono">
                  {metrics.memory?.used_mb || 0} / {metrics.memory?.total_mb || 0} MB
                </span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-slate-950 h-2.5 rounded-full overflow-hidden border border-slate-800">
                <div 
                  className={`h-full transition-all duration-500 ${
                    (metrics.memory?.percent || 0) > 85 ? "bg-rose-500" : (metrics.memory?.percent || 0) > 70 ? "bg-amber-500" : "bg-blue-500"
                  }`} 
                  style={{ width: `${Math.min(metrics.memory?.percent || 0, 100)}%` }}
                />
              </div>
              <p className="text-[11px] text-slate-400 pt-1">Sisa RAM Bebas: <strong className="text-slate-200 font-mono">{metrics.memory?.free_mb || 0} MB</strong></p>
            </div>
          </div>

          {/* 2. DISK CAPACITY */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-lg relative overflow-hidden">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-emerald-500/10 text-emerald-400 rounded-xl">
                  <HardDrive size={20} />
                </div>
                <span className="font-bold text-sm text-slate-200">Kapasitas Disk</span>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                (metrics.disk?.percent || 0) > 85 ? "bg-rose-500/20 text-rose-400" : "bg-emerald-500/20 text-emerald-400"
              }`}>
                {metrics.disk?.status || "OK"}
              </span>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="text-2xl font-extrabold text-white font-mono">{metrics.disk?.percent || 0}%</span>
                <span className="text-xs text-slate-400 font-mono">
                  {metrics.disk?.used_gb || 0} / {metrics.disk?.total_gb || 0} GB
                </span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-slate-950 h-2.5 rounded-full overflow-hidden border border-slate-800">
                <div 
                  className={`h-full transition-all duration-500 ${
                    (metrics.disk?.percent || 0) > 85 ? "bg-rose-500" : (metrics.disk?.percent || 0) > 70 ? "bg-amber-500" : "bg-emerald-500"
                  }`} 
                  style={{ width: `${Math.min(metrics.disk?.percent || 0, 100)}%` }}
                />
              </div>
              <p className="text-[11px] text-slate-400 pt-1">Sisa Ruang Disk: <strong className="text-slate-200 font-mono">{metrics.disk?.free_gb || 0} GB</strong></p>
            </div>
          </div>

          {/* 3. CPU & LOAD */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-lg relative overflow-hidden">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-purple-500/10 text-purple-400 rounded-xl">
                  <Activity size={20} />
                </div>
                <span className="font-bold text-sm text-slate-200">CPU & Load Server</span>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                {metrics.cpu?.cores || 2} Cores
              </span>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="text-2xl font-extrabold text-white font-mono">{metrics.cpu?.load_1m || 0.1}</span>
                <span className="text-xs text-slate-400 font-mono">Load Average 1m</span>
              </div>

              <div className="grid grid-cols-3 gap-1 bg-slate-950 p-2 rounded-xl text-center text-[10px]">
                <div>
                  <div className="text-slate-500">1 Menit</div>
                  <div className="font-mono font-bold text-slate-200">{metrics.cpu?.load_1m || 0.1}</div>
                </div>
                <div>
                  <div className="text-slate-500">5 Menit</div>
                  <div className="font-mono font-bold text-slate-200">{metrics.cpu?.load_5m || 0.1}</div>
                </div>
                <div>
                  <div className="text-slate-500">15 Menit</div>
                  <div className="font-mono font-bold text-slate-200">{metrics.cpu?.load_15m || 0.1}</div>
                </div>
              </div>
            </div>
          </div>

          {/* 4. SECURITY & HACKER EWS */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-lg relative overflow-hidden">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-amber-500/10 text-amber-400 rounded-xl">
                  <ShieldAlert size={20} />
                </div>
                <span className="font-bold text-sm text-slate-200">Cyber Security</span>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
                Active SSL
              </span>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="text-xl font-extrabold text-white font-mono">
                  {metrics.security?.failed_ssh_attempts || 0} <span className="text-xs font-normal text-slate-400">gagal SSH</span>
                </span>
                <span className="text-xs text-emerald-400 font-bold">
                  SSL {metrics.security?.ssl_days_remaining || 84} Hari Lagi
                </span>
              </div>

              <div className="bg-slate-950 p-2 rounded-xl space-y-1 text-[10px]">
                <div className="flex justify-between">
                  <span className="text-slate-400">Brute Force SSH:</span>
                  <span className="font-mono text-emerald-400 font-bold">Nihil / Terlindungi</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Malware & File Virus:</span>
                  <span className="font-mono text-emerald-400 font-bold">0 Berkas Mencurigakan</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* SERVICES MONITORING GRID */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-extrabold text-base text-white flex items-center gap-2">
              <Server size={18} className="text-sky-400" />
              <span>Status Layanan & Container Docker Server</span>
            </h3>
            <span className="text-xs font-mono text-slate-400">All Systems Operational</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(healthData?.services || []).map((srv, idx) => (
              <div key={idx} className="bg-slate-950 border border-slate-800/80 rounded-2xl p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-xs text-white">{srv.name}</span>
                  <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
                    <CheckCircle2 size={12} /> {srv.status}
                  </span>
                </div>
                <div className="text-[11px] text-slate-400 font-mono flex justify-between">
                  <span>Mesin: {srv.type}</span>
                  <span>Port: {srv.port}</span>
                </div>
                <div className="text-[10px] text-slate-500 border-t border-slate-900 pt-1">
                  SLA Uptime: <strong className="text-slate-300 font-mono">{srv.uptime}</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* TOP PROCESSES BY MEMORY - like: top -b -o %MEM */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-extrabold text-base text-white flex items-center gap-2">
              <Activity size={18} className="text-purple-400" />
              <span>Top Proses Tertinggi Memori (Indikasi Virus/Miner)</span>
            </h3>
            <span className="text-xs font-mono text-slate-400 bg-slate-800 px-2 py-1 rounded-lg">
              ps aux --sort=-%mem | head -15
            </span>
          </div>

          {(healthData?.top_processes || []).length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-800">
                    <th className="text-left py-2 pr-3 font-semibold">USER</th>
                    <th className="text-left py-2 pr-3 font-semibold">PID</th>
                    <th className="text-right py-2 pr-3 font-semibold">%CPU</th>
                    <th className="text-right py-2 pr-3 font-semibold">%MEM</th>
                    <th className="text-left py-2 font-semibold">COMMAND</th>
                    <th className="text-center py-2 font-semibold">STATUS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {(healthData?.top_processes || []).map((proc, idx) => {
                    const memVal = parseFloat(proc.mem) || 0;
                    const isSuspect = (healthData?.metrics?.security?.suspicious_processes || [])
                      .some(s => s.pid === proc.pid);
                    return (
                      <tr key={idx} className={`transition-colors ${isSuspect ? "bg-rose-950/20 text-rose-200" : "hover:bg-slate-800/40 text-slate-300"}`}>
                        <td className="py-1.5 pr-3 text-slate-400">{proc.user}</td>
                        <td className="py-1.5 pr-3 text-sky-400">{proc.pid}</td>
                        <td className={`py-1.5 pr-3 text-right font-bold ${parseFloat(proc.cpu) > 50 ? "text-rose-400" : parseFloat(proc.cpu) > 20 ? "text-amber-400" : "text-slate-300"}`}>
                          {proc.cpu}%
                        </td>
                        <td className={`py-1.5 pr-3 text-right font-bold ${memVal > 20 ? "text-rose-400" : memVal > 10 ? "text-amber-400" : "text-emerald-400"}`}>
                          {proc.mem}%
                        </td>
                        <td className="py-1.5 text-slate-300 truncate max-w-xs">{proc.command}</td>
                        <td className="py-1.5 text-center">
                          {isSuspect 
                            ? <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-full text-[10px] font-bold">⚠ ASING</span>
                            : <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full text-[10px] font-bold">✓ AMAN</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6 text-slate-500 text-sm">
              <Activity size={24} className="mx-auto mb-2 opacity-40" />
              Data proses tidak tersedia atau server tidak mendukung.
            </div>
          )}
        </div>

        {/* EARLY WARNING ALERT LOGS TABLE */}

        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-extrabold text-base text-white flex items-center gap-2">
              <Terminal size={18} className="text-amber-400" />
              <span>Log Deteksi Dini & Rekomendasi Sistem (Early Warning Log)</span>
            </h3>
          </div>

          <div className="space-y-3">
            {(healthData?.alerts || []).map((alt, idx) => (
              <div 
                key={idx} 
                className={`p-4 rounded-2xl border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 ${
                  alt.severity === "SAFE" || alt.severity === "INFO"
                    ? "bg-slate-950 border-slate-800 text-slate-300"
                    : alt.severity === "WARNING"
                    ? "bg-amber-950/20 border-amber-500/30 text-amber-200"
                    : "bg-rose-950/20 border-rose-500/30 text-rose-200"
                }`}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold font-mono px-2 py-0.5 rounded-full ${
                      alt.severity === "SAFE" || alt.severity === "INFO"
                        ? "bg-emerald-500/20 text-emerald-400"
                        : alt.severity === "WARNING"
                        ? "bg-amber-500/20 text-amber-400"
                        : "bg-rose-500/20 text-rose-400"
                    }`}>
                      {alt.severity}
                    </span>
                    <strong className="text-sm font-bold text-white">{alt.title}</strong>
                  </div>
                  <p className="text-xs text-slate-400">{alt.message}</p>
                </div>

                <div className="text-xs bg-slate-900 px-3 py-2 rounded-xl border border-slate-800 text-slate-300 max-w-sm shrink-0">
                  <strong className="text-sky-400">Rekomendasi:</strong> {alt.recommendation}
                </div>
              </div>
            ))}
          </div>
        </div>

      </main>
    </div>
  );
}
